/**
 * Privilege release — V98-4-3 / D-98-3; PRD G-4 ("Rationale is privileged by
 * default, and releasing it is logged with a waiver acknowledgment");
 * research §7 item 7.
 *
 * Applicability rationale is legal analysis. It leaves the workspace only
 * through a RELEASE that Legal records: the recipient (who, what kind, why),
 * the determinations it covers, an explicit waiver acknowledgment (stored
 * with its text) and an expiry. Releases are logged (`ai_program_events`),
 * revoked once with a reason, never edited or deleted (migration 238).
 *
 * The export (`buildDeterminationsExport`) carries OUTCOMES ONLY unless it is
 * produced under an active release: then — and only then — the rationale of
 * the determinations that release names is included, stamped with the
 * release. Every export is logged with the release it used (or none). A
 * revoked or expired release releases nothing.
 *
 * Authorization is the route's: the legal reviewer releases / revokes /
 * lists; the export is owner / admin or the legal reviewer.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { aiProgramEvent, getCurrentDecision } from './obligation-source-service.js';
import { canonicalJson, sha256Hex } from './certification-package-service.js';
import { buildGrid, cellId } from './applicability-determination-service.js';
import { queryInChunks } from '../utils/sql-in.js';
export const RELEASE_RECIPIENT_KINDS = ['auditor', 'trust_center', 'regulator', 'customer', 'counsel', 'other'];
/** Stored verbatim with every release the legal reviewer acknowledges. */
export const PRIVILEGE_WAIVER_TEXT = 'I understand that releasing this legal analysis to the recipient may waive attorney–client privilege or work-product protection for it. I am authorized to release it.';
/** A release lasts at most a year; renew by releasing again. */
export const MAX_RELEASE_DAYS = 365;
export class PrivilegeReleaseError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'PrivilegeReleaseError';
    }
}
const text = (v, max) => {
    if (typeof v !== 'string')
        return null;
    const t = v.trim();
    return t ? t.slice(0, max) : null;
};
const statusOf = (r, now) => r.revoked_at ? 'revoked' : r.expires_at <= now.toISOString() ? 'expired' : 'active';
export async function createRelease(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const ws = input.workspace_id;
    if (input.waiver_acknowledged !== true)
        throw new PrivilegeReleaseError('invalid', 'Acknowledge the privilege waiver to release the rationale.');
    if (!RELEASE_RECIPIENT_KINDS.includes(input.recipient_kind))
        throw new PrivilegeReleaseError('invalid', 'Unknown recipient kind.');
    const name = text(input.recipient_name, 200);
    const purpose = text(input.purpose, 500);
    if (!name)
        throw new PrivilegeReleaseError('invalid', 'Name the recipient.');
    if (!purpose)
        throw new PrivilegeReleaseError('invalid', 'Say what the release is for.');
    const email = input.recipient_email ? text(input.recipient_email, 254) : null;
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        throw new PrivilegeReleaseError('invalid', 'The recipient email is not valid.');
    const expires = Date.parse(input.expires_at);
    if (!Number.isFinite(expires) || expires <= now.getTime())
        throw new PrivilegeReleaseError('invalid', 'The expiry must be in the future.');
    if (expires > now.getTime() + MAX_RELEASE_DAYS * 86_400_000)
        throw new PrivilegeReleaseError('invalid', `A release lasts at most ${MAX_RELEASE_DAYS} days.`);
    const ids = [...new Set(input.determination_ids)];
    if (ids.length === 0 || ids.length > 200)
        throw new PrivilegeReleaseError('invalid', 'Choose the determinations to release (1–200).');
    // Up to 200 ids — past D1's 100-parameter limit — on the tenant plane (may be Postgres): chunked.
    const rows = await queryInChunks(db, ids, (ph, chunk) => ({
        sql: `SELECT determination_id, state, system_id, law_key, outcome, eu_role, eu_risk_tier, conditions, reason, decision_version, decided_at
       FROM applicability_determinations
      WHERE workspace_id = ? AND determination_id IN (${ph})`,
        params: [ws, ...chunk],
    }));
    // Another workspace's id reads as not found (no enumeration).
    if (rows.length !== ids.length)
        throw new PrivilegeReleaseError('not_found', 'Determination not found.');
    // V98-7-3 M4: only RECORDED rows (immutable) — a decided row is still editable, and its later text must never
    // ride on this waiver. Each item keeps the exact released text + its hash; the export returns that snapshot.
    if (rows.some((r) => r.state !== 'recorded' || !r.reason)) {
        throw new PrivilegeReleaseError('conflict', 'Only recorded determinations with a reason can be released.');
    }
    const snapshots = new Map();
    for (const r of rows) {
        const json = canonicalJson(snapshotOf(r));
        snapshots.set(r.determination_id, { json, sha: await sha256Hex(json) });
    }
    const id = (input.newId ?? (() => generateRandomId('prl')))();
    const expiresIso = new Date(expires).toISOString();
    await db.batch([
        {
            sql: `INSERT INTO privilege_releases (release_id, workspace_id, recipient_kind, recipient_name, recipient_email, purpose, waiver_acknowledged,
              waiver_text, expires_at, released_by, released_at) VALUES (?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?)`,
            params: [id, ws, input.recipient_kind, name, email, purpose, PRIVILEGE_WAIVER_TEXT, expiresIso, input.actor_user_id, at],
        },
        ...ids.map((d) => ({
            sql: `INSERT INTO privilege_release_items (release_id, determination_id, workspace_id, snapshot_json, snapshot_sha256) VALUES (?, ?, ?, ?, ?)`,
            params: [id, d, ws, snapshots.get(d).json, snapshots.get(d).sha],
        })),
        aiProgramEvent(ws, { kind: 'privilege_release', id }, 'privilege_released', input.actor_user_id, at, {
            detail: { recipient_kind: input.recipient_kind, recipient_name: name, purpose, determination_ids: ids, expires_at: expiresIso, waiver_acknowledged: true },
        }),
    ]);
    return (await getRelease(db, ws, id, now));
}
const snapshotOf = (r) => ({
    determination_id: r.determination_id, system_id: r.system_id, law_key: r.law_key, outcome: r.outcome, eu_role: r.eu_role,
    eu_risk_tier: r.eu_risk_tier, conditions: r.conditions ?? null, reason: r.reason ?? null, decision_version: r.decision_version ?? null, decided_at: r.decided_at ?? null,
});
/** The verified snapshots of a release (an item without one, or whose hash doesn't match, releases nothing). */
async function releasedSnapshots(db, ws, releaseId) {
    const items = (await db.query(`SELECT determination_id, snapshot_json, snapshot_sha256 FROM privilege_release_items WHERE workspace_id = ? AND release_id = ?`, [ws, releaseId])).results;
    const out = new Map();
    for (const i of items) {
        if (!i.snapshot_json || !i.snapshot_sha256 || (await sha256Hex(i.snapshot_json)) !== i.snapshot_sha256)
            continue;
        try {
            out.set(i.determination_id, JSON.parse(i.snapshot_json));
        }
        catch { /* releases nothing */ }
    }
    return out;
}
export async function getRelease(db, ws, id, now = new Date()) {
    const r = (await db.query(`SELECT * FROM privilege_releases WHERE workspace_id = ? AND release_id = ? LIMIT 1`, [ws, id])).results[0];
    if (!r)
        return null;
    const items = (await db.query(`SELECT determination_id FROM privilege_release_items WHERE workspace_id = ? AND release_id = ? ORDER BY determination_id`, [ws, id])).results;
    return { ...r, determination_ids: items.map((i) => i.determination_id), status: statusOf(r, now) };
}
export async function listReleases(db, ws, now = new Date()) {
    const rows = (await db.query(`SELECT * FROM privilege_releases WHERE workspace_id = ? ORDER BY released_at DESC, release_id DESC`, [ws])).results;
    const items = (await db.query(`SELECT release_id, determination_id FROM privilege_release_items WHERE workspace_id = ? ORDER BY determination_id`, [ws])).results;
    return rows.map((r) => ({ ...r, determination_ids: items.filter((i) => i.release_id === r.release_id).map((i) => i.determination_id), status: statusOf(r, now) }));
}
export async function revokeRelease(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const reason = text(input.reason, 500);
    if (!reason)
        throw new PrivilegeReleaseError('invalid', 'Say why the release is revoked.');
    const r = await getRelease(db, input.workspace_id, input.release_id, now);
    if (!r)
        throw new PrivilegeReleaseError('not_found', 'Release not found.');
    if (r.revoked_at)
        throw new PrivilegeReleaseError('conflict', 'This release is already revoked.');
    await db.batch([
        {
            sql: `UPDATE privilege_releases SET revoked_at = ?, revoked_by = ?, revoked_reason = ? WHERE release_id = ? AND workspace_id = ? AND revoked_at IS NULL`,
            params: [at, input.actor_user_id, reason, r.release_id, input.workspace_id],
        },
        aiProgramEvent(input.workspace_id, { kind: 'privilege_release', id: r.release_id }, 'privilege_revoked', input.actor_user_id, at, { reason }),
    ]);
    return (await getRelease(db, input.workspace_id, r.release_id, now));
}
/**
 * Auditor / trust-center export of the RECORDED determinations. Outcomes
 * only, unless `release_id` names an active release in this workspace: then
 * the rationale of the determinations it covers is included. Logged.
 */
export async function buildDeterminationsExport(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const ws = input.workspace_id;
    let release = null;
    if (input.release_id) {
        release = await getRelease(db, ws, input.release_id, now);
        if (!release)
            throw new PrivilegeReleaseError('not_found', 'Release not found.');
        if (release.status !== 'active')
            throw new PrivilegeReleaseError('not_active', `This release is ${release.status}; it releases nothing.`);
    }
    // V98-7-3 M4: the rationale comes from the release's snapshot, never the live row.
    const snaps = release ? await releasedSnapshots(db, ws, release.release_id) : new Map();
    const released = new Set([...snaps.keys()]);
    const grid = await buildGrid(db, ws, now, { scope: 'effective' });
    const current = await getCurrentDecision(db, ws);
    const names = new Map((grid?.systems ?? []).map((s) => [s.system_id, s.name]));
    const rows = (await db.query(`SELECT d.*, s.name AS system_name FROM applicability_determinations d LEFT JOIN ai_systems s ON s.system_id = d.system_id
      WHERE d.workspace_id = ? AND d.state = 'recorded' ORDER BY d.jurisdiction, d.law_label, s.name, d.system_id`, [ws])).results;
    const determinations = rows.filter((d) => !!d.outcome).map((d) => {
        const out = {
            determination_id: d.determination_id, law: d.law_label, jurisdiction: d.jurisdiction, system: names.get(d.system_id) ?? d.system_name ?? d.system_id,
            outcome: d.outcome, eu_role: d.eu_role, eu_risk_tier: d.eu_risk_tier, decision_version: d.decision_version, decided_at: d.decided_at,
            rationale_released: released.has(d.determination_id),
        };
        const snap = snaps.get(d.determination_id);
        return snap ? { ...out, reason: snap.reason, conditions: snap.conditions } : out;
    });
    await db.batch([aiProgramEvent(ws, { kind: 'workspace', id: ws }, 'determinations_exported', input.actor_user_id, at, {
            detail: { release_id: release?.release_id ?? null, determinations: determinations.length, rationale_released: determinations.filter((d) => d.rationale_released).length,
                cells: rows.map((d) => cellId(d.system_id, d.law_key)) },
        })]);
    return {
        kind: 'gacpro.ai-regulatory.determinations', schema_version: 1, generated_at: at, decision_version: current?.version ?? null,
        release: release ? {
            release_id: release.release_id, recipient_kind: release.recipient_kind, recipient_name: release.recipient_name, purpose: release.purpose,
            expires_at: release.expires_at, released_by: release.released_by, released_at: release.released_at,
        } : null,
        notice: release
            ? `Rationale released to ${release.recipient_name} for ${release.purpose} until ${release.expires_at.slice(0, 10)}. Privileged & confidential otherwise.`
            : 'Outcomes only. Legal’s rationale is privileged and is not included.',
        determinations,
    };
}
//# sourceMappingURL=privilege-release-service.js.map